import { useState, type FormEvent } from "react";

import { connectors, type Connector, type ConnectorEnrollment } from "../lib/api.js";
import { relativeTime } from "../lib/freshness.js";
import { useLiveData } from "../lib/use-live-data.js";
import { useNow } from "../lib/use-now.js";

/**
 * Connectors: the appliances that give us a way into a customer's network.
 *
 * The enrollment instructions are shown once, on creation, and the token in
 * them expires within the hour. That is deliberate -- it is pasted into a
 * router's terminal, so it ends up in a clipboard and a command history, and a
 * short life is what limits how long that matters.
 */

const REFRESH_MS = 20_000;

export function ConnectorsPage() {
  const now = useNow();
  const { data, error, reload } = useLiveData(() => connectors.list(), REFRESH_MS);
  const [enrollment, setEnrollment] = useState<(ConnectorEnrollment & { name: string }) | null>(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);

  const canManage = data?.canManage ?? false;

  const create = async (event: FormEvent) => {
    event.preventDefault();
    setActionError(null);
    try {
      const result = await connectors.create(name);
      setEnrollment({ ...result, name });
      setName("");
      setCreating(false);
      reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Could not create connector");
    }
  };

  const remove = async (connector: Connector) => {
    if (!confirm(`Delete "${connector.name}"? Its tunnel drops within seconds.`)) return;
    setActionError(null);
    try {
      await connectors.remove(connector.id);
      reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Could not delete");
    }
  };

  return (
    <div>
      <h1 style={{ fontSize: 15, margin: "0 0 6px", letterSpacing: "0.14em" }}>&gt; CONNECTORS</h1>
      <p style={help}>
        A small router on your network that dials out to Router Report, so
        devices can be monitored without opening anything inbound. It takes a
        DHCP address like any other host and needs no changes to your network.
      </p>

      {actionError && <p style={{ color: "var(--status-critical)", fontSize: 13 }}>{`!! ${actionError}`}</p>}

      {enrollment && <Instructions enrollment={enrollment} onDismiss={() => setEnrollment(null)} />}

      {error && !data && <p style={{ color: "var(--status-critical)" }}>{`!! ${error}`}</p>}
      {!data && !error && <p style={{ color: "var(--text-muted)" }}>LOADING...</p>}

      {data && data.connectors.length === 0 && !creating && (
        <p style={{ color: "var(--text-muted)", fontSize: 13 }}>-- NONE YET --</p>
      )}

      {data && data.connectors.length > 0 && (
        <div style={{ border: "1px solid var(--border)", background: "var(--surface-1)", marginBottom: 14 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "left", borderBottom: "1px solid var(--border)" }}>
                {["NAME", "STATE", "TUNNEL", "ON THEIR LAN", "DEVICES", ...(canManage ? [""] : [])].map(
                  (h, i) => <th key={h || `a${i}`} style={th}>{h}</th>,
                )}
              </tr>
            </thead>
            <tbody>
              {data.connectors.map((connector) => (
                <tr key={connector.id} style={{ borderBottom: "1px solid var(--gridline)" }}>
                  <td style={td}>
                    {connector.name}
                    {connector.identity && (
                      <span style={{ display: "block", color: "var(--text-muted)", fontSize: 11 }}>
                        {connector.identity}
                        {connector.routeros_version ? ` · RouterOS ${connector.routeros_version}` : ""}
                      </span>
                    )}
                  </td>
                  <td style={td}><State connector={connector} now={now} /></td>
                  <td style={{ ...td, color: "var(--text-secondary)", fontSize: 12 }}>
                    {connector.tunnel_address ?? "—"}
                  </td>
                  <td style={{ ...td, color: "var(--text-secondary)", fontSize: 12 }}>
                    {connector.lan_address ?? "—"}
                  </td>
                  <td style={{ ...td, color: "var(--text-secondary)" }}>{connector.device_count}</td>
                  {canManage && (
                    <td style={{ ...td, textAlign: "right" }}>
                      <button onClick={() => void remove(connector)} style={{ ...smallButton, color: "var(--status-critical)" }}>
                        [ DELETE ]
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {canManage && (creating ? (
        <form onSubmit={(e) => void create(e)} style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
          <div>
            <label style={label}>&gt; CONNECTOR NAME</label>
            <input autoFocus required value={name} onChange={(e) => setName(e.target.value)}
              placeholder="the site it will live at" style={{ ...input, width: 260 }} />
          </div>
          <button type="submit" style={{ ...smallButton, padding: "8px 14px" }}>[ CREATE ]</button>
          <button type="button" onClick={() => setCreating(false)} style={{ ...smallButton, padding: "8px 14px" }}>
            [ CANCEL ]
          </button>
        </form>
      ) : (
        <button onClick={() => setCreating(true)} style={{ ...smallButton, padding: "6px 12px" }}>
          [ + NEW CONNECTOR ]
        </button>
      ))}
    </div>
  );
}

/**
 * State, with the distinction that matters: an appliance that has never
 * connected is not the same as one that has stopped. The first is an
 * enrollment that was not completed; the second is a site that has gone dark.
 */
function State({ connector, now }: { connector: Connector; now: number }) {
  if (connector.awaiting_enrollment) {
    return (
      <span style={{ color: "var(--status-warning)", fontSize: 12 }}>
        [AWAITING] <span style={{ color: "var(--text-muted)" }}>not yet run</span>
      </span>
    );
  }
  if (!connector.enrolled_at) {
    return <span style={{ color: "var(--status-critical)", fontSize: 12 }}>[EXPIRED]</span>;
  }
  if (connector.status === "online") {
    return (
      <span style={{ color: "var(--status-good)", fontSize: 12 }}>
        [ONLINE] <span style={{ color: "var(--text-muted)" }}>
          {relativeTime(connector.last_handshake_at, now)}
        </span>
      </span>
    );
  }
  return (
    <span style={{ color: "var(--status-critical)", fontSize: 12 }}>
      [OFFLINE] <span style={{ color: "var(--text-muted)" }}>
        {connector.last_handshake_at ? relativeTime(connector.last_handshake_at, now) : "never connected"}
      </span>
    </span>
  );
}

function Instructions({
  enrollment,
  onDismiss,
}: {
  enrollment: ConnectorEnrollment & { name: string };
  onDismiss: () => void;
}) {
  return (
    <div
      style={{
        border: "1px solid var(--status-warning)",
        background: "color-mix(in srgb, var(--status-warning) 10%, var(--surface-1))",
        padding: 14,
        marginBottom: 16,
        fontSize: 13,
      }}
    >
      <div style={{ color: "var(--status-warning)", letterSpacing: "0.1em", marginBottom: 8 }}>
        ENROLL {enrollment.name.toUpperCase()}
      </div>

      <ol style={{ margin: "0 0 10px", paddingLeft: 18, lineHeight: 1.7, color: "var(--text-secondary)" }}>
        <li>Plug the router's <strong>ether1</strong> into your network.</li>
        <li>Open its terminal (Winbox or WebFig) and paste the line below. It is
          one line — paste it whole.</li>
        <li>
          It erases itself, reboots, and comes back configured. Nothing else on
          the router is kept — <strong>do not use one that is doing anything
          else.</strong>
        </li>
      </ol>
      <p style={{ color: "var(--text-muted)", fontSize: 11, margin: "0 0 8px", lineHeight: 1.6 }}>
        Works on a factory-default router or one already wiped: it brings up a
        DHCP client if there is none, waits for an address, and refuses to reset
        if it cannot download its configuration first.
      </p>

      <code
        style={{
          display: "block",
          whiteSpace: "pre-wrap",
          wordBreak: "break-all",
          fontSize: 12,
          padding: "10px 12px",
          background: "var(--page-plane)",
          border: "1px solid var(--border)",
          userSelect: "all",
          lineHeight: 1.6,
        }}
      >
        {enrollment.bootstrap}
      </code>

      <p style={{ color: "var(--text-muted)", fontSize: 11, margin: "8px 0 0", lineHeight: 1.6 }}>
        Shown once. The link expires {new Date(enrollment.expires_at).toLocaleString()} and works a
        single time — if it lapses, delete this connector and make another. Its
        tunnel address will be <code>{enrollment.tunnel_address}</code>.
      </p>

      <button onClick={onDismiss} style={{ ...smallButton, marginTop: 10 }}>[ DISMISS ]</button>
    </div>
  );
}

const help: React.CSSProperties = { color: "var(--text-muted)", fontSize: 11, margin: "0 0 14px", lineHeight: 1.6 };
const th: React.CSSProperties = { padding: "10px 14px", color: "var(--text-muted)", fontWeight: 500 };
const td: React.CSSProperties = { padding: "10px 14px" };
const input: React.CSSProperties = {
  padding: "7px 9px", fontSize: 13, border: "1px solid var(--border)",
  background: "var(--surface-raised)", color: "var(--text-primary)",
};
const label: React.CSSProperties = {
  display: "block", fontSize: 11, color: "var(--text-secondary)", marginBottom: 4, letterSpacing: "0.08em",
};
const smallButton: React.CSSProperties = {
  background: "none", border: "1px solid var(--border)", padding: "5px 9px",
  fontSize: 11, color: "var(--text-secondary)", cursor: "pointer", letterSpacing: "0.08em",
};
